/**
 * Personal project and bundle management with inline edits, colors, priorities, archiving,
 * creation, deletion and merging. Imported bundles that still need a purpose are listed in a banner
 * and must be given one. Mutations use the workspace API and refresh the shared workspace store so
 * selectors and task breadcrumbs receive updated data.
 *
 * @module apps/web/src/pages/WorkspacePage
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, FolderKanban, GitMerge, Layers, Plus, Trash2 } from 'lucide-react';
import type { WorkspaceBundleInput, WorkspaceProjectInput } from '@fluxability/types';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { bundlesNeedingPurpose, useWorkspaceStore } from '@/stores/workspaceStore';
import { BundlePurposeDialog } from '@/components/workspace/BundlePurposeDialog';
import { MergeBundleDialog } from '@/components/workspace/MergeBundleDialog';
import { cn } from '@/lib/utils';

/** Fixed color choices shared by workspace creation and inline entity editing. */
const palette = ['#8b8a84', '#2563eb', '#0f766e', '#b45309', '#be123c', '#7c3aed'];

/**
 * Personal project and bundle management with inline edits, colors, priorities, archiving,
 * creation, and deletion. Mutations use the workspace API and refresh the shared workspace store so
 * selectors and task breadcrumbs receive updated data.
 */
export function WorkspacePage() {
  const projects = useWorkspaceStore(s => s.projects);
  const bundles = useWorkspaceStore(s => s.bundles);
  const loadWorkspace = useWorkspaceStore(s => s.loadWorkspace);
  const [projectDraft, setProjectDraft] = useState({ name: '', purpose: '', color: '#2563eb' });
  const [bundleDraft, setBundleDraft] = useState({
    name: '',
    purpose: '',
    color: '#0f766e',
    project_id: 'none',
  });
  const [saving, setSaving] = useState(false);
  const [purposeBundleId, setPurposeBundleId] = useState<string | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    loadWorkspace().catch(error => console.error('Failed to load workspace', error));
  }, [loadWorkspace]);

  const activeProjects = projects.filter(project => project.status === 'active');
  const activeBundles = bundles.filter(bundle => bundle.status === 'active');
  const needingPurpose = useMemo(() => bundlesNeedingPurpose(bundles), [bundles]);
  const purposeBundle = bundles.find(bundle => bundle.id === purposeBundleId) || null;
  const mergeSource = bundles.find(bundle => bundle.id === mergeSourceId) || null;
  const bundlesByProject = useMemo(() => {
    const map = new Map<string, number>();
    for (const bundle of activeBundles) {
      if (!bundle.projectId) continue;
      map.set(bundle.projectId, (map.get(bundle.projectId) || 0) + 1);
    }
    return map;
  }, [activeBundles]);

  async function createProject() {
    if (!projectDraft.name.trim()) return;
    setSaving(true);
    try {
      await api.workspace.createProject(projectDraft);
      setProjectDraft({ name: '', purpose: '', color: '#2563eb' });
      await loadWorkspace();
    } finally {
      setSaving(false);
    }
  }

  async function createBundle() {
    if (!bundleDraft.name.trim()) return;
    setSaving(true);
    try {
      await api.workspace.createBundle({
        ...bundleDraft,
        project_id: bundleDraft.project_id === 'none' ? null : bundleDraft.project_id,
      });
      setBundleDraft({ name: '', purpose: '', color: '#0f766e', project_id: 'none' });
      await loadWorkspace();
    } finally {
      setSaving(false);
    }
  }

  async function updateProject(id: string, updates: Partial<WorkspaceProjectInput>) {
    await api.workspace.updateProject(id, updates);
    await loadWorkspace();
  }

  async function updateBundle(id: string, updates: Partial<WorkspaceBundleInput>) {
    const response = await api.workspace.updateBundle(id, {
      ...updates,
      project_id: updates.project_id === 'none' ? null : updates.project_id,
    });
    setNotice(response.success ? null : response.error?.message || 'The bundle was not saved.');
    await loadWorkspace();
  }

  async function deleteProject(id: string) {
    await api.workspace.deleteProject(id);
    await loadWorkspace();
  }

  async function deleteBundle(id: string) {
    await api.workspace.deleteBundle(id);
    await loadWorkspace();
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5">
      <header className="border-b pb-4">
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <span>↗</span>
          <span>Workspace</span>
        </div>
        <h1 className="mt-2 text-3xl font-semibold tracking-normal">Activity system</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
          Create the bundles and projects you want to plan with. Every activity belongs to a
          user-created bundle; projects stay optional.
        </p>
      </header>

      {needingPurpose.length > 0 && (
        <section
          aria-label="Imported bundles that need a purpose"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold">
                {needingPurpose.length === 1
                  ? '1 imported bundle needs a purpose'
                  : `${needingPurpose.length} imported bundles need a purpose`}
              </h2>
              <p className="mt-1 text-sm opacity-90">
                Say what each one is for before you add activities to it. Imports keep arriving
                while you decide.
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {needingPurpose.map(bundle => (
                  <li key={bundle.id}>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-background h-8"
                      onClick={() => setPurposeBundleId(bundle.id)}
                    >
                      {bundle.name}: add purpose
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
      {notice && (
        <p role="alert" className="text-destructive text-sm">
          {notice}
        </p>
      )}
      {status && (
        <p role="status" className="text-muted-foreground text-sm">
          {status}
        </p>
      )}

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <DatabaseHeader icon={Layers} title="Bundles" count={activeBundles.length} />
          <div className="bg-card overflow-hidden rounded-md border">
            <div className="bg-muted/40 text-muted-foreground grid grid-cols-[minmax(160px,1fr)_minmax(140px,220px)_120px_40px_40px] gap-2 border-b px-3 py-2 text-xs">
              <span>Name</span>
              <span>Project</span>
              <span>Status</span>
              <span />
              <span />
            </div>
            {activeBundles.length === 0 && (
              <div className="text-muted-foreground px-3 py-8 text-sm">
                Create your first bundle before adding activities.
              </div>
            )}
            {activeBundles.map(bundle => (
              <div
                key={bundle.id}
                className="grid grid-cols-[minmax(160px,1fr)_minmax(140px,220px)_120px_40px_40px] items-center gap-2 border-b px-3 py-2 last:border-b-0"
              >
                <InlineName
                  value={bundle.name}
                  purpose={bundle.purpose || ''}
                  purposeRequired={Boolean(bundle.importSource)}
                  color={bundle.color}
                  onName={name => updateBundle(bundle.id, { name })}
                  onPurpose={purpose => updateBundle(bundle.id, { purpose })}
                  onColor={color => updateBundle(bundle.id, { color })}
                />
                <Select
                  value={bundle.projectId || 'none'}
                  onValueChange={project_id => updateBundle(bundle.id, { project_id })}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {activeProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {bundle.needsPurpose ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-fit border-amber-300 px-2 text-xs"
                    onClick={() => setPurposeBundleId(bundle.id)}
                  >
                    Needs purpose
                  </Button>
                ) : (
                  <Badge variant="secondary" className="w-fit">
                    {bundle.importSource === 'canvas' ? 'Canvas' : 'Active'}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMergeSourceId(bundle.id)}
                  disabled={activeBundles.length < 2}
                  aria-label={`Merge ${bundle.name} into another bundle`}
                  title="Merge into…"
                >
                  <GitMerge className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteBundle(bundle.id)}
                  aria-label={`Delete ${bundle.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <CreationPanel
          title="New bundle"
          action="Add bundle"
          saving={saving}
          onSubmit={createBundle}
        >
          <DraftFields
            name={bundleDraft.name}
            purpose={bundleDraft.purpose}
            color={bundleDraft.color}
            onChange={updates => setBundleDraft(prev => ({ ...prev, ...updates }))}
          />
          <div className="grid gap-2">
            <Label>Project</Label>
            <Select
              value={bundleDraft.project_id}
              onValueChange={project_id => setBundleDraft(prev => ({ ...prev, project_id }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {activeProjects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CreationPanel>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <DatabaseHeader icon={FolderKanban} title="Projects" count={activeProjects.length} />
          <div className="bg-card overflow-hidden rounded-md border">
            <div className="bg-muted/40 text-muted-foreground grid grid-cols-[minmax(160px,1fr)_120px_80px_40px] gap-2 border-b px-3 py-2 text-xs">
              <span>Name</span>
              <span>Bundles</span>
              <span>Status</span>
              <span />
            </div>
            {activeProjects.map(project => (
              <div
                key={project.id}
                className="grid grid-cols-[minmax(160px,1fr)_120px_80px_40px] items-center gap-2 border-b px-3 py-2 last:border-b-0"
              >
                <InlineName
                  value={project.name}
                  purpose={project.purpose || ''}
                  color={project.color}
                  onName={name => updateProject(project.id, { name })}
                  onPurpose={purpose => updateProject(project.id, { purpose })}
                  onColor={color => updateProject(project.id, { color })}
                />
                <span className="text-muted-foreground text-sm">
                  {bundlesByProject.get(project.id) || 0}
                </span>
                <Badge variant="outline" className="w-fit">
                  Active
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteProject(project.id)}
                  aria-label={`Delete ${project.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <CreationPanel
          title="New project"
          action="Add project"
          saving={saving}
          onSubmit={createProject}
        >
          <DraftFields
            name={projectDraft.name}
            purpose={projectDraft.purpose}
            color={projectDraft.color}
            onChange={updates => setProjectDraft(prev => ({ ...prev, ...updates }))}
          />
        </CreationPanel>
      </section>
      <BundlePurposeDialog
        bundle={purposeBundle}
        onCancel={() => setPurposeBundleId(null)}
        onSaved={() => setPurposeBundleId(null)}
      />
      <MergeBundleDialog
        source={mergeSource}
        onClose={() => setMergeSourceId(null)}
        onMerged={(count, target) =>
          setStatus(
            `Moved ${count} ${count === 1 ? 'activity' : 'activities'} into ${target.name}.`
          )
        }
      />
    </div>
  );
}

/** Label a workspace entity collection with its icon and current item count. */
function DatabaseHeader({
  icon: Icon,
  title,
  count,
}: {
  /** Icon component rendered beside the visible caption. */
  icon: LucideIcon;
  /** Visible heading for this panel or collection. */
  title: string;
  /** Number of entities in the displayed collection. */
  count: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="text-muted-foreground h-4 w-4" />
      <h2 className="text-lg font-semibold">{title}</h2>
      <span className="text-muted-foreground text-xs">{count}</span>
    </div>
  );
}

/** Keep local name and purpose drafts responsive while reporting changed trimmed values on blur. Incoming props resynchronize the drafts; clearing purpose emits null unless it is required, in which case the draft reverts; empty names are not submitted. */
function InlineName({
  value,
  purpose,
  purposeRequired = false,
  color,
  onName,
  onPurpose,
  onColor,
}: {
  /** Current persisted entity name used to initialize and resynchronize the local draft. */
  value: string;
  /** Current entity purpose used to initialize and resynchronize the local draft. */
  purpose: string;
  /** Whether the purpose may not be cleared (imported bundles). Defaults to false. */
  purposeRequired?: boolean;
  /** Current palette color for the workspace entity. */
  color: string;
  /** Persist a nonempty trimmed name after a changed field loses focus. */
  onName: (name: string) => void;
  /** Persist a trimmed purpose, or null when the purpose is cleared. */
  onPurpose: (purpose: string | null) => void;
  /** Persist the selected palette color immediately. */
  onColor: (color: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [purposeDraft, setPurposeDraft] = useState(purpose);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    setPurposeDraft(purpose);
  }, [purpose]);

  return (
    <div className="flex min-w-0 items-start gap-2">
      <ColorMenu value={color} onChange={onColor} />
      <div className="min-w-0 flex-1">
        <Input
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onBlur={() => draft.trim() && draft !== value && onName(draft.trim())}
          className="hover:border-input focus-visible:border-ring h-8 border-transparent bg-transparent px-1 text-sm shadow-none"
        />
        <Input
          value={purposeDraft}
          onChange={event => setPurposeDraft(event.target.value)}
          onBlur={() => {
            const next = purposeDraft.trim();
            if (!next && purposeRequired) {
              setPurposeDraft(purpose);
              return;
            }
            if (next !== purpose) onPurpose(next || null);
          }}
          required={purposeRequired}
          aria-required={purposeRequired}
          placeholder={purposeRequired ? 'What is this bundle for? (required)' : 'Purpose'}
          className="text-muted-foreground hover:border-input focus-visible:border-ring h-7 border-transparent bg-transparent px-1 text-xs shadow-none"
        />
      </div>
    </div>
  );
}

/** Compose new workspace-entity fields and a submit action. Disable submission while the parent’s save is pending; validation and persistence belong to that owner. */
function CreationPanel({
  title,
  action,
  saving,
  children,
  onSubmit,
}: {
  /** Visible heading for this panel or collection. */
  title: string;
  /** Visible label of the creation button. */
  action: string;
  /** Whether a creation request is pending; disables the submit button. */
  saving: boolean;
  /** Controls or content supplied by the owning view. */
  children: ReactNode;
  /** Ask the owner to validate and create the current draft. */
  onSubmit: () => void;
}) {
  return (
    <aside className="bg-muted/20 rounded-md border p-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="mt-3 grid gap-3">{children}</div>
      <Button onClick={onSubmit} disabled={saving} className="mt-3 w-full">
        <Plus className="mr-2 h-4 w-4" />
        {action}
      </Button>
    </aside>
  );
}

/** Edit the name, purpose, and palette color of an unsaved workspace entity. Emit partial patches so each control preserves the other draft fields. */
function DraftFields({
  name,
  purpose,
  color,
  onChange,
}: {
  /** Team display name used for labels and the fallback identity initial. */
  name: string;
  /** Draft purpose text for the workspace entity. */
  purpose: string;
  /** Current palette color for the workspace entity. */
  color: string;
  /** Merge the emitted name, purpose, or color patch into the parent draft. */
  onChange: (updates: {
    /** Unsaved project or bundle display name. */ name?: string;
    /** Unsaved explanation of the project or bundle's intended outcome. */ purpose?: string;
    /** Unsaved display color selected for the taxonomy record. */ color?: string;
  }) => void;
}) {
  return (
    <>
      <div className="grid gap-2">
        <Label>Name</Label>
        <Input value={name} onChange={event => onChange({ name: event.target.value })} />
      </div>
      <div className="grid gap-2">
        <Label>Purpose</Label>
        <Textarea
          value={purpose}
          onChange={event => onChange({ purpose: event.target.value })}
          className="min-h-20"
        />
      </div>
      <div className="grid gap-2">
        <Label>Color</Label>
        <div className="flex flex-wrap gap-1.5">
          {palette.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => onChange({ color: option })}
              className={cn(
                'h-7 w-7 rounded-md border',
                color === option && 'ring-ring ring-2 ring-offset-2'
              )}
              style={{ backgroundColor: option }}
              aria-label={`Use ${option}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

/** Show the current entity color and select another entry from the fixed workspace palette. */
function ColorMenu({
  value,
  onChange,
}: {
  /** Currently selected palette color. */
  value: string; /** Receive the selected palette color for persistence by the owner. */
  onChange: (color: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {palette.slice(0, 1).map(() => (
        <button
          key={value}
          type="button"
          className="h-4 w-4 shrink-0 rounded-sm border"
          style={{ backgroundColor: value }}
          aria-label="Current color"
        />
      ))}
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className="bg-background h-7 w-8 rounded border text-transparent"
        aria-label="Change color"
      >
        {palette.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}
